import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import fileImage from '../../assets/img/file.png';
import { Button, Checkbox, Img, Text } from '../../Atoms';
import { DOCUMENT_UPLOAD_ACCEPT } from '../../Config/Config';
import { Input, Upload } from '../../Molecules';
import { validateDocumentFile } from '../../Validation';
import { OrganismSelectContact } from '../index';
import SendDocumentErrors from './SendDocumentErrors';

/*
Situation de l'enfant → Type de document requis
*/
const typeDocumentMaintienEnfantOptions = [
  {
    id: 0,
    value: {
      docToSend: 'Avis imposition + certificat scolarité',
      idDocToSend: 0,
      situation: 'En étude',
      nbDocObligatoire: 2,
    },
    text: 'En étude'
  },
  {
    id: 1,
    value: {
      docToSend: 'Contrat de travail',
      idDocToSend: 6,
      situation: 'Apprenti/Alternance',
      nbDocObligatoire: 1,
    },
    text: 'Apprenti/Alternance'
  },
  {
    id: 2,
    value: {
      docToSend: 'Avis d\'imposition + Notification de rejet d’indemnisation de Pôle Emploi',
      idDocToSend: 1,
      situation: 'Primo demandeur d\'emploi',
      nbDocObligatoire: 2,
    },
    text: 'Primo demandeur d\'emploi'
  },
  {
    id: 3,
    value: {
      docToSend: 'Carte d\'invalidité ou notification MDPH',
      idDocToSend: 2,
      situation: 'Enfant handicapé',
      nbDocObligatoire: 1,
    },
    text: 'Enfant handicapé'
  },
  {
    id: 4,
    value: {
      docToSend: 'Avis d\'imposition',
      idDocToSend: 3,
      situation: 'Enfant fiscalement à charge',
      nbDocObligatoire: 1,
    },
    text: 'Enfant fiscalement à charge'
  },
  {
    id: 5,
    value: {
      docToSend: 'Carte de volontaire',
      idDocToSend: 4,
      situation: 'Service civique',
      nbDocObligatoire: 1,
    },
    text: 'Service civique'
  },
  {
    id: 6,
    value: {
      docToSend: 'Autres',
      idDocToSend: 5,
      situation: 'Autres',
      nbDocObligatoire: 1,
    },
    text: 'Autres'
  }
];

const getCurrentSchoolYear = () => {
  const currentDate = new Date();
  const getSchoolYear = (date) => {
    return date.getMonth() >= 7 ? date.getFullYear() : date.getFullYear() - 1;
  };
  const schoolYear = getSchoolYear(currentDate);
  return {
    id: 0,
    value: `${schoolYear}`,
    text: `${schoolYear}/${schoolYear + 1}`
  };
};

const MaintienEnfantStep1 = ({
  benefItems,
  onChangeSelect,
  onChange,
  selectFile,
  files,
  onSubmit,
  description,
  textInfoBenef,
  sendDocumentErrors
}) => {
  const [selectedDocType, setSelectedDocType] = useState('-');
  // On récupère l'objet de l'année scolaire directement
  const schoolYear = getCurrentSchoolYear();
  const [isMaintainChildSupportAgreement, setMaintainChildSupportAgreement] = useState(false);
  const [isBeneficiaireSet, setIsBeneficiaireSet] = useState(false);
  const [isSchoolYearSet, setIsSchoolYearSet] = useState(true); // Directement à true
  const [isSituationSet, setIsSituationSet] = useState(false);
  const [isNbFile, setIsNbFile] = useState(false); // Initialisé à false

  // Initialisation de l'état pour stocker les valeurs sélectionnées
  const [selectedValues, setSelectedValues] = useState({
    benefitaire: null,
    // --- MODIFICATION ICI : L'année scolaire est "sélectionnée" dès le départ ---
    schoolYear: schoolYear.value,
    situation: null,
    typedoc: null,
  });

  const [docsWarningMessage, setDocsWarningMessage] = useState('');

  const handleSelectChange = (text, field, options) => {
    const selectedOption = findOptionByText(text, options);
    if (selectedOption) {
      const { value, text } = selectedOption;
      onChangeSelect({ target: { value, text } }, field);
      setSelectedValues(prevValues => ({
        ...prevValues,
        [field]: value
      }));
    }
  };

  const updateIsNbFile = (situation, files) => {
    if (situation) {
      // La condition est maintenant basée sur le nombre de documents requis et le nombre de fichiers téléchargés
      setIsNbFile(files.length >= situation.nbDocObligatoire);
    } else {
      setIsNbFile(false);
    }
  };

  // s'exécute une seule fois au montage du composant
  useEffect(() => {
    // On appelle la fonction du parent pour lui transmettre l'année scolaire
    // qui est maintenant fixe et connue dès le départ.
    onChangeSelect({ target: { value: schoolYear.value } }, 'schoolYear');
  }, []); // Le tableau de dépendances vide assure que cet effet ne s'exécute qu'une fois.

  useEffect(() => {
    setIsBeneficiaireSet(!!selectedValues.benefitaire);
    setIsSituationSet(!!selectedValues.situation);
    //  Plus besoin de gérer isSchoolYearSet ici, c'est toujours vrai

    if (selectedValues.situation) {
      setSelectedDocType(selectedValues.situation.docToSend);
      updateIsNbFile(selectedValues.situation, files); // Mettre à jour le statut des fichiers

      const requiredDocsCount = selectedValues.situation.nbDocObligatoire;
      const uploadedDocsCount = files.length;

      if (requiredDocsCount >= 2 && uploadedDocsCount === 1) {
        setDocsWarningMessage(`Il semblerait qu'un document soit manquant pour valider votre envoi`);
      } else {
        setDocsWarningMessage('');
      }
    } else {
      setSelectedDocType('-');
      setIsNbFile(false); // Réinitialiser si aucune situation n'est sélectionnée
      setDocsWarningMessage('');
    }
  }, [selectedValues, files]);

  // La fonction de validation est maintenant plus simple
  const areAllFieldsSet = () => {
    return isBeneficiaireSet && isSchoolYearSet && isSituationSet && isNbFile;
  };

  const generateDocToSendOptions = () => {
    return typeDocumentMaintienEnfantOptions.map(option => ({
      id: option.id,
      value: option.value,
      text: option.value.docToSend
    }));
  };

  const findOptionByText = (text, options) => {
    return options.find(opt => opt.text === text);
  };

  const toggleAgreement = () => {
    setMaintainChildSupportAgreement(!isMaintainChildSupportAgreement);
  };

  return (
    <div>
      <Row className="row-document">
        <Col xs={6} className="col-document">
          <Text text="Bénéficiaire" className="label-input-document" />
          <OrganismSelectContact
            containerClassName="select-document-container"
            placeholder=""
            iconClassName="icon-select-document"
            onChange={(value) => handleSelectChange(value, 'benefitaire', benefItems)}
            containerMobileOption="container-option-mobile-document"
            subTitleMobileClassName="sub-title-select-document"
            headerClassName="header-select-document"
            options={benefItems}
            iconSize={'7px'}
          />
        </Col>
        <Col xs={6} className="col-document">
          <Text text="Année scolaire" className="label-input-document" />
          {/* Le champ Input reste non modifiable et affiche la valeur textuelle */}
          <Input
            value={schoolYear.text} // Affiche la valeur textuelle, ex: "2025/2026"
            disabled={true}
            className="select-document-container"
          />
        </Col>

        {textInfoBenef && (
          <Col xs={12} lg={12} className="col-document">
            <Text text={textInfoBenef} className={'doc-red-text'} type="H6"/>
          </Col>
        )}

        <Col xs={12} className="col-document"> {/* Utilise toute la largeur pour une meilleure lisibilité */}
          <Text text="Situation de votre enfant" className="label-input-document" />
          <OrganismSelectContact
            containerClassName="select-document-container"
            placeholder=""
            iconClassName="icon-select-document"
            onChange={(value) => handleSelectChange(value, 'situation', typeDocumentMaintienEnfantOptions)}
            containerMobileOption="container-option-mobile-document"
            titleMobile="Situation de votre enfant"
            subTitleMobileClassName="sub-title-select-document"
            header="Situation de votre enfant"
            headerClassName="header-select-document"
            options={typeDocumentMaintienEnfantOptions}
            iconSize={'7px'}
          />
        </Col>
        <Col xs={12} className="col-document"> {/* Utilise toute la largeur */}
          <Text text="Type de document (Merci de vous référer au courrier reçu par mail)" className="label-input-document" />
          <OrganismSelectContact
            containerClassName="select-document-container"
            placeholder=""
            iconClassName="icon-select-document"
            onChange={(value) => handleSelectChange(value, 'typedoc', generateDocToSendOptions())}
            containerMobileOption="container-option-mobile-document"
            titleMobile="Document requis"
            subTitleMobileClassName="sub-title-select-document"
            header="Document requis"
            headerClassName="header-select-document"
            value={selectedDocType}
            options={generateDocToSendOptions()}
            iconSize={'7px'}
          />
        </Col>

        <Col xs={12} className="col-document"> {/* Utilise toute la largeur */}
          <div className="container-spaced">
            <Checkbox
              type="checkbox"
              size={14}
              checked={isMaintainChildSupportAgreement}
              onClick={toggleAgreement}
            />
            <div className="m-3">
              <Text text="Je certifie sur l'honneur l'exactitude des renseignements portés sur cette déclaration" type="h6"/>
            </div>
          </div>
        </Col>
      </Row>

      {docsWarningMessage && (
        <Row className="row-document">
          <Col xs={12}>
            <Text text={docsWarningMessage} className="text-h6 doc-red-text" />
          </Col>
        </Row>
      )}

      <Upload
        handleFileChange={selectFile}
        accept={DOCUMENT_UPLOAD_ACCEPT}
        validateFile={validateDocumentFile}
        id="document"
        contentClassName={'input-file-document'}
        style={{ height: '1px' }}
        uploadContent={
          <div className="content-input-file-document">
            <Img src={fileImage} className="img-file-upload" />
            <div>
              Glissez et déposez vos fichiers ici, ou{' '}
              <span>téléchargez-les</span> depuis votre ordinateur
            </div>
          </div>
        }
      />

      <Input
        className="input-decription-document input-description-medicine"
        onChange={(e) => onChange(e)}
        value={description}
        as="textarea"
        placeholder="Laissez-nous un message qui décrit votre besoin (facultatif)"
      />

      <SendDocumentErrors errors={sendDocumentErrors} />

      {isMaintainChildSupportAgreement && areAllFieldsSet() ? (
        <div className="container-btn-send-doc">
          <Button
            text="Envoyer"
            className="btn-send-doc btn-send-doc-medicine"
            onClick={onSubmit}
            resetHover={true}
          />
        </div>
      ) : (
        <div className="container-btn-send-doc">
          <Button
            text="Envoyer"
            className="btn-send-doc btn-send-doc-medicine"
            disabled={true}
            resetHover={true}
          />
        </div>
      )}
    </div>
  );
};

MaintienEnfantStep1.propTypes = {
  benefItems: PropTypes.array,
  onChangeSelect: PropTypes.func,
  onChange: PropTypes.func,
  selectFile: PropTypes.func,
  files: PropTypes.array,
  onSubmit: PropTypes.func,
  description: PropTypes.string,
  textInfoBenef: PropTypes.string,
  sendDocumentErrors: PropTypes.arrayOf(PropTypes.string)
};

export default MaintienEnfantStep1;
